import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  clearAuthStorage,
  getCurrentUser,
  getStoredToken,
  getStoredUser,
  login as loginRequest,
  logout as logoutRequest,
  register as registerRequest,
  setStoredToken,
  setStoredUser
} from "../services/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(getStoredUser());
  const [token, setToken] = useState(getStoredToken());
  const [isBootstrapping, setIsBootstrapping] = useState(true);

  const isAuthenticated = Boolean(token && user);

  const applyAuthState = useCallback((nextToken, nextUser) => {
    setToken(nextToken || null);
    setUser(nextUser || null);
    setStoredToken(nextToken || null);
    setStoredUser(nextUser || null);
  }, []);

  const login = useCallback(
    async (payload) => {
      const response = await loginRequest(payload);
      applyAuthState(response.token, response.user);
      return response;
    },
    [applyAuthState]
  );

  const register = useCallback(
    async (payload) => {
      const response = await registerRequest(payload);
      applyAuthState(response.token, response.user);
      return response;
    },
    [applyAuthState]
  );

  const logout = useCallback(async () => {
    try {
      if (getStoredToken()) {
        await logoutRequest();
      }
    } finally {
      clearAuthStorage();
      setToken(null);
      setUser(null);
    }
  }, []);

  const setAuthUser = useCallback((nextUser) => {
    setUser(nextUser || null);
    setStoredUser(nextUser || null);
  }, []);

  useEffect(() => {
    if (!token) {
      setIsBootstrapping(false);
      return;
    }

    getCurrentUser()
      .then((response) => {
        if (!response?.user) {
          throw new Error("No user in profile response.");
        }
        applyAuthState(token, response.user);
      })
      .catch(() => {
        clearAuthStorage();
        setToken(null);
        setUser(null);
      })
      .finally(() => {
        setIsBootstrapping(false);
      });
  }, [applyAuthState, token]);

  const value = useMemo(
    () => ({
      user,
      token,
      isAuthenticated,
      isBootstrapping,
      login,
      register,
      logout,
      setAuthUser
    }),
    [user, token, isAuthenticated, isBootstrapping, login, register, logout, setAuthUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider.");
  }
  return context;
}
